import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CalendarClock, CircleCheck, CircleX, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { BUSINESS, formatTime12 } from "../lib/data";

const STATUS_LABELS = {
  booked: { text: "Booked", cls: "bg-gold/20 text-gold-dark border-gold/40" },
  confirmed: { text: "Confirmed", cls: "bg-forest/10 text-forest border-forest/30" },
  completed: { text: "Completed", cls: "bg-sage/20 text-moss border-sage/40" },
  cancelled: { text: "Cancelled", cls: "bg-destructive/10 text-destructive border-destructive/30" },
};

function formatDay(dateStr) {
  return new Date(`${dateStr}T12:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

function nextDays(count) {
  const days = [];
  const today = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    days.push({ value: `${yyyy}-${mm}-${dd}`, weekday: d.toLocaleDateString("en-US", { weekday: "short" }), day: d.getDate(), month: d.toLocaleDateString("en-US", { month: "short" }) });
  }
  return days;
}

function Reschedule({ booking, onDone }) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [slots, setSlots] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const days = useMemo(() => nextDays(28), []);

  useEffect(() => {
    if (!date) return;
    setLoading(true);
    setSlots(null);
    setTime("");
    api.get("/slots", { params: { date, service_id: booking.service_id } })
      .then((res) => setSlots(res.data.slots))
      .catch(() => setSlots([]))
      .finally(() => setLoading(false));
  }, [date, booking.service_id]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/manage/${booking.id}/reschedule`, { date, time });
      toast.success("Your appointment has been rescheduled");
      onDone(data);
    } catch (e) {
      toast.error(apiError(e));
      if (e?.response?.status === 409) setTime("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="reschedule-panel" className="mt-6 rounded-lg border border-linen bg-ivory p-6">
      <h3 className="font-serif text-xl text-forest">Pick a new time</h3>
      <div className="mt-5 flex gap-2 overflow-x-auto pb-3" data-lenis-prevent>
        {days.map((d) => (
          <button
            key={d.value}
            data-testid={`reschedule-day-${d.value}`}
            onClick={() => setDate(d.value)}
            className={`flex w-[64px] shrink-0 flex-col items-center rounded-md border px-2 py-2.5 transition-colors duration-300 ${date === d.value ? "border-gold bg-forest text-ivory" : "border-linen bg-ivory text-forest hover:border-gold/60"}`}
          >
            <span className={`text-[10px] font-semibold uppercase tracking-widest ${date === d.value ? "text-gold" : "text-moss"}`}>{d.weekday}</span>
            <span className="mt-0.5 font-serif text-lg">{d.day}</span>
            <span className={`text-[10px] ${date === d.value ? "text-ivory/70" : "text-moss/70"}`}>{d.month}</span>
          </button>
        ))}
      </div>
      <div className="mt-4 min-h-[80px]">
        {!date && <p className="text-sm text-moss">Select a day above to see available times.</p>}
        {date && loading && <p className="text-sm text-moss">Checking availability&hellip;</p>}
        {date && slots && slots.length === 0 && !loading && (
          <p data-testid="reschedule-slots-empty" className="rounded-md border border-linen bg-cream p-4 text-sm text-moss">No openings that day. Try another day, or call {BUSINESS.phone}.</p>
        )}
        {date && slots && slots.length > 0 && (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
            {slots.map((t) => (
              <button
                key={t}
                data-testid={`reschedule-slot-${t.replace(":", "")}`}
                onClick={() => setTime(t)}
                className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors duration-300 ${time === t ? "border-gold bg-gold text-forest-dark" : "border-linen bg-ivory text-forest hover:border-gold/60"}`}
              >
                {formatTime12(t)}
              </button>
            ))}
          </div>
        )}
      </div>
      <button
        data-testid="reschedule-confirm-button"
        disabled={!date || !time || busy}
        onClick={confirm}
        className="mt-5 rounded-md bg-forest px-6 py-2.5 text-sm font-semibold text-ivory transition-colors duration-300 hover:bg-forest-dark disabled:cursor-not-allowed disabled:opacity-40"
      >
        {busy ? "Rescheduling..." : "Confirm New Time"}
      </button>
    </div>
  );
}

export default function ManageBooking() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(() => {
    api.get(`/manage/${bookingId}`).then((res) => setBooking(res.data)).catch(() => setNotFound(true));
  }, [bookingId]);
  useEffect(load, [load]);

  const cancel = async () => {
    if (!window.confirm("Cancel this appointment? Your time slot will be released.")) return;
    setCancelling(true);
    try {
      const { data } = await api.post(`/manage/${bookingId}/cancel`);
      setBooking(data);
      setShowReschedule(false);
      toast.success("Your appointment has been cancelled");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCancelling(false);
    }
  };

  const status = booking ? STATUS_LABELS[booking.status] || STATUS_LABELS.booked : null;

  return (
    <main data-testid="manage-booking-page" className="min-h-screen bg-ivory px-5 pb-24 pt-16 sm:px-8">
      <div className="mx-auto max-w-2xl">
        <Link to="/" className="flex items-center gap-1.5 text-sm font-semibold text-moss transition-colors hover:text-forest" data-testid="manage-back-home">
          <ChevronLeft size={15} /> Back to home
        </Link>

        {notFound && (
          <div data-testid="manage-not-found" className="mt-10 rounded-lg border border-linen bg-ivory p-10 text-center">
            <h1 className="font-serif text-3xl text-forest">Booking not found</h1>
            <p className="mt-3 text-sm text-moss">We couldn&rsquo;t find that reservation. Please check your link, or call us at {BUSINESS.phone}.</p>
          </div>
        )}

        {!notFound && !booking && <p className="mt-10 text-sm text-moss">Loading your booking&hellip;</p>}

        {booking && (
          <>
            <div className="mt-6 rounded-lg border border-linen bg-ivory p-7 sm:p-9">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold-dark">Your Appointment</p>
                  <h1 className="mt-2 font-serif text-3xl text-forest">{booking.service_name}</h1>
                </div>
                <span data-testid="manage-status-badge" className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold ${status.cls}`}>
                  {status.text}
                </span>
              </div>

              <dl className="mt-6 space-y-2.5 text-sm text-moss">
                <div className="flex justify-between"><dt>When</dt><dd className="font-medium text-forest" data-testid="manage-when">{formatDay(booking.date)} at {formatTime12(booking.time)}</dd></div>
                <div className="flex justify-between"><dt>Visit type</dt><dd className="font-medium text-forest">{booking.visit_type_label}</dd></div>
                <div className="flex justify-between"><dt>Price</dt><dd className="font-medium text-forest">${booking.price} — pay at your visit</dd></div>
                <div className="flex justify-between"><dt>Booked for</dt><dd className="font-medium text-forest">{booking.name}</dd></div>
              </dl>

              {booking.visit_type === "in_person" && booking.status !== "cancelled" && (
                <p className="mt-5 border-t border-linen pt-4 text-sm text-moss">{BUSINESS.addressLines.join(" · ")}</p>
              )}

              {booking.status === "cancelled" && (
                <div data-testid="manage-cancelled-note" className="mt-6 flex items-start gap-3 rounded-md border border-linen bg-cream p-4 text-sm text-moss">
                  <CircleX size={18} className="mt-0.5 shrink-0 text-destructive" />
                  <span>This appointment has been cancelled. You&rsquo;re welcome to <Link to="/book" className="font-semibold text-forest underline">book a new visit</Link> anytime.</span>
                </div>
              )}

              {booking.can_modify && (
                <div className="mt-8 flex flex-wrap gap-3">
                  <button
                    data-testid="manage-reschedule-button"
                    onClick={() => setShowReschedule((v) => !v)}
                    className="flex items-center gap-2 rounded-md bg-forest px-6 py-3 text-sm font-semibold text-ivory transition-colors duration-300 hover:bg-forest-dark"
                  >
                    <CalendarClock size={15} /> {showReschedule ? "Hide Reschedule" : "Reschedule"}
                  </button>
                  <button
                    data-testid="manage-cancel-button"
                    onClick={cancel}
                    disabled={cancelling}
                    className="rounded-md border border-destructive/40 px-6 py-3 text-sm font-semibold text-destructive transition-colors duration-300 hover:bg-destructive/10 disabled:opacity-50"
                  >
                    {cancelling ? "Cancelling..." : "Cancel Appointment"}
                  </button>
                </div>
              )}

              {!booking.can_modify && booking.status !== "cancelled" && (
                <p className="mt-6 text-sm text-moss">Need to make a change? Call or text us at <span className="font-semibold text-forest">{BUSINESS.phone}</span>.</p>
              )}
            </div>

            {showReschedule && booking.can_modify && (
              <Reschedule
                booking={booking}
                onDone={(data) => {
                  setBooking(data);
                  setShowReschedule(false);
                }}
              />
            )}

            {booking.status !== "cancelled" && (
              <p className="mt-6 flex items-center justify-center gap-2 text-xs text-moss/70">
                <CircleCheck size={13} className="text-forest" /> No payment needed online — you pay at your visit.
              </p>
            )}
          </>
        )}
      </div>
    </main>
  );
}
